import React, { useContext, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Keyboard, TouchableOpacity, View } from 'react-native'
import { Controller, useForm } from 'react-hook-form'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Input, Text } from '@components/base'
import { ScrollableAvoidKeyboard } from '@components'
import { SecureInput } from '@components/inputs/SecureInput'
import { NavigationContainer } from '@screens/Common/containers'
import { NavigatorContext, useGoBack, useSnack, Validators } from '@utils'
import { userSelector } from '@src/selectors/app'

const normalizeEmail = (value?: string) => (value || '').trim().toLowerCase()

export const EditEmail = () => {
  const styles = useStyleSheet(themedStyle)
  const dispatch = useDispatch()
  const goBack = useGoBack()
  const showSnack = useSnack()
  const insets = useSafeAreaInsets()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)

  const user = useSelector(userSelector)
  const email = user?.email

  const isSubmitting = useRef(false)
  const isResetting = useRef(false)

  const {
    control,
    formState: { errors, isValid },
    setFocus,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
      currentPassword: '',
    },
  })

  const onResetPress = () => {
    if (!email || isResetting.current) {
      return
    }

    isResetting.current = true

    dispatch({
      type: 'users/resetPasswordRequest',
      payload: { email },
      success: () => showSnack(`We've sent a password reset link to ${email}`),
      failure: () => showSnack('Failed to send reset link, please try later', null, 'error'),
      complete: () => {
        isResetting.current = false
      },
    })
  }

  const onSubmit = handleSubmit(({ email: newEmail, currentPassword }) => {
    Keyboard.dismiss()

    if (isSubmitting.current) {
      return
    }

    const normalizedEmail = normalizeEmail(newEmail)

    isSubmitting.current = true
    enableLoader()

    dispatch({
      type: 'users/updateEmail',
      payload: {
        email: normalizedEmail,
        currentPassword,
      },
      // The backend stages the change and emails a confirmation link; the login
      // email only updates once the user confirms.
      success: () => {
        showSnack(
          `We've sent a confirmation link to ${normalizedEmail} — your email updates once you confirm it`,
        )
        goBack()
      },
      failure: (error: any) => {
        showSnack(error?.message || 'Failed to update email, please try later', null, 'error')
      },
      complete: () => {
        disableLoader()
        isSubmitting.current = false
      },
    })
  })

  return (
    <NavigationContainer style={styles.container} title="Edit Email" goBack={goBack}>
      <ScrollableAvoidKeyboard
        style={styles.container}
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
        keyboardShouldPersistTaps="handled"
      >
        <View>
          <Controller
            control={control}
            name="email"
            rules={{ required: true, validate: Validators.EmailValidator }}
            render={({ field }) => (
              <Input
                {...field}
                autoCapitalize="none"
                autoCorrect={false}
                hasError={!!errors.email}
                keyboardType="email-address"
                label="New Email"
                onChangeText={(text) => field.onChange(text.trim())}
                placeholder="Enter your new email address"
                returnKeyType="next"
                style={styles.input}
                onSubmitEditing={() => setFocus('currentPassword')}
              />
            )}
          />
          <View>
            <Controller
              control={control}
              name="currentPassword"
              rules={{ required: true }}
              render={({ field }) => (
                <SecureInput
                  {...field}
                  hasError={!!errors.currentPassword}
                  label="Current Password"
                  caption="Required to change your email"
                  placeholder="Enter your current password"
                  returnKeyType="done"
                  style={styles.input}
                  onSubmitEditing={onSubmit}
                />
              )}
            />
            <TouchableOpacity
              accessibilityLabel="Forgot or don't have a password? Reset it"
              disabled={!email}
              onPress={onResetPress}
            >
              <Text type="small" bold style={styles.resetLink}>
                Forgot or don&apos;t have a password? Reset it
              </Text>
            </TouchableOpacity>
          </View>
        </View>
        <Button
          type="primary"
          size="block"
          disabled={!isValid}
          onPress={onSubmit}
          accessibilityLabel="Send confirmation link"
          allowPressWhenDisabled
        >
          Send Confirmation Link
        </Button>
      </ScrollableAvoidKeyboard>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  content: {
    justifyContent: 'space-between',
    alignSelf: 'center',
    width: '100%',
    maxWidth: 600,
    paddingHorizontal: 16,
  },
  input: {
    marginTop: 24,
    marginBottom: 16,
  },
  resetLink: {
    marginBottom: 8,
    color: 'theme.text.link',
  },
})
